/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  serverTimestamp,
  setDoc,
  updateDoc,
  addDoc,
} from "firebase/firestore";
import { db } from "../firebase";

import PrintableSinglesScoreSheet from "./PrintableSinglesScoreSheet";
import PrintableTeamsScoreSheet from "./PrintableTeamsScoreSheet";

function convertToISODate(dateStr) {
  const [dd, mm, yyyy] = dateStr.split("/");
  return `${yyyy}-${mm}-${dd}`;
}

function getWeekIndex(weekId) {
  if (!weekId) return 0;
  const match = weekId.match(/week-(\d+)/);
  return match ? Number(match[1]) : 0;
}

async function fetchCourtNumbersForDate(isoDate) {
  const q = query(collection(db, "Bookings"), where("date", "==", isoDate));
  const snap = await getDocs(q);
  const mapping = {};
  snap.forEach((docSnap) => {
    const data = docSnap.data();
    if (data.drawMatchId) {
      mapping[data.drawMatchId] = data.courtNumber;
    }
  });
  return mapping;
}

export default function BulkPrintableScoreSheets() {
  const [searchParams] = useSearchParams();

  const [weeksToPrint, setWeeksToPrint] = useState([]);

  const [pennantDataMap, setPennantDataMap] = useState({});

  const [fillInsMap, setFillInsMap] = useState({});

  const [mergedWeeksToPrint, setMergedWeeksToPrint] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const raw = searchParams.get("allWeeks");
    if (!raw) {
      setLoading(false);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setWeeksToPrint(parsed);
    } catch (e) {
      console.error("Failed to parse allWeeks JSON:", e);
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!weeksToPrint.length) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const uniqueIds = [...new Set(weeksToPrint.map((item) => item.pennantId))];
        const promises = uniqueIds.map(async (pid) => {
          const snap = await getDoc(doc(db, "Pennants", pid));
          return { pid, data: snap.exists() ? snap.data() : null };
        });
        const results = await Promise.all(promises);
        const map = results.reduce((acc, { pid, data }) => {
          if (data) acc[pid] = data;
          return acc;
        }, {});
        setPennantDataMap(map);
      } catch (err) {
        console.error("Error fetching pennant docs:", err);
      }
    })();
  }, [weeksToPrint]);

  useEffect(() => {
    if (!weeksToPrint.length) return;
    (async () => {
      try {
        const uniqueIds = [...new Set(weeksToPrint.map((item) => item.pennantId))];
        const promises = uniqueIds.map(async (pid) => {
          const fillInSnap = await getDocs(collection(db, "Pennants", pid, "fillInRequired"));
          const mapping = {};
          fillInSnap.forEach((docSnap) => {
            const data = docSnap.data();
            if (data.memberId) {
              mapping[data.memberId] = data;
            }
          });
          return { pid, mapping };
        });
        const results = await Promise.all(promises);
        const combined = results.reduce((acc, { pid, mapping }) => {
          acc[pid] = mapping;
          return acc;
        }, {});
        setFillInsMap(combined);
      } catch (err) {
        console.error("Error fetching fillIns:", err);
      }
    })();
  }, [weeksToPrint]);

  useEffect(() => {
    async function mergeCourtNumbers() {
      if (!weeksToPrint.length) return;

      const isoDates = weeksToPrint.map((item) => convertToISODate(item.weekData.date));
      const uniqueIsoDates = [...new Set(isoDates)];
      const dateMappingPromises = uniqueIsoDates.map(async (isoDate) => {
        const mapping = await fetchCourtNumbersForDate(isoDate);
        return { isoDate, mapping };
      });
      const dateMappings = await Promise.all(dateMappingPromises);
      const isoDateToMapping = dateMappings.reduce((acc, { isoDate, mapping }) => {
        acc[isoDate] = mapping;
        return acc;
      }, {});
      const merged = weeksToPrint.map((item) => {
        const { pennantId, weekData } = item;
        const isoDate = convertToISODate(weekData.date);
        const courtMapping = isoDateToMapping[isoDate] || {};
        const weekIndex = getWeekIndex(weekData.id);
        const mergedMatches = (weekData.matches || []).map((match, mIndex) => {
          const drawMatchId = `pennant-${pennantId}-week-${weekIndex}-match-${mIndex}`;
          return {
            ...match,
            courtNumber:
              (courtMapping && courtMapping[drawMatchId]) ||
              match.courtNumber ||
              "...",
            id: drawMatchId,
          };
        });
        return { ...item, weekData: { ...weekData, matches: mergedMatches } };
      });
      setMergedWeeksToPrint(merged);
    }
    mergeCourtNumbers();
  }, [weeksToPrint]);

  const handlePrint = () => window.print();

  if (loading) {
    return <Typography>Loading bulk prints...</Typography>;
  }
  if (!mergedWeeksToPrint.length) {
    return <Typography>No weeks to print.</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>

      <Box className="no-print" sx={{ mb: 2, textAlign: "center" }}>
        <Button variant="contained" onClick={handlePrint}>
          Print All
        </Button>
      </Box>
      {mergedWeeksToPrint.map((item, idx) => {
        const { pennantId, weekData } = item;
        const pData = pennantDataMap[pennantId];

        const fillIns = fillInsMap[pennantId] || {};
        if (!pData) {
          return (
            <Box key={idx} sx={{ mb: 4, border: "1px solid #ddd", p: 2 }}>
              <Typography variant="body2" color="error">
                Loading...
              </Typography>
            </Box>
          );
        }
        const isTeams = pData.participantType === "Teams";
        return (
          <Box
            key={idx}
            sx={{
              pageBreakAfter: "always",
              mb: 4,
              border: "1px solid #ccc",
              p: 2,
            }}
          >
            {isTeams ? (
              <PrintableTeamsScoreSheet
                pennantData={pData}
                weekData={weekData}
                fillIns={fillIns}
              />
            ) : (
              <PrintableSinglesScoreSheet
                pennantData={pData}
                weekData={weekData}
                fillIns={fillIns}
              />
            )}
          </Box>
        );
      })}
      <style>
        {`
          @media print {
            @page {
              margin: 8mm;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
}

BulkPrintableScoreSheets.propTypes = {
};
